import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import { getAdminCatalogPrintings } from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import { CatalogThumbnail } from '@/components/catalog-thumbnail'
import { QueryErrorState } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { ArchiveCatalogRecordButton } from './-components/archive-catalog-record-button'
import { CreatePrintingDialog } from './-components/create-printing-dialog'
import { DebouncedSearchInput } from './-components/debounced-search-input'
import { FilterSelect } from './-components/filter-select'
import { ResetFiltersButton } from './-components/reset-filters-button'

const PRINTING_LANGUAGES = ['en', 'fr', 'ja', 'zh-Hans', 'ko'] as const
const PRINTING_VARIANTS = [
	'normal',
	'parallel',
	'manga',
	'manga_super',
	'special_art',
	'promo',
	'reprint'
] as const
const PRINTING_STATUSES = ['released', 'announced'] as const

export const Route = createFileRoute('/printings')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	validateSearch: z.object({
		direction: z.enum(['asc', 'desc']).optional(),
		expansionCode: z.string().max(16).optional(),
		language: z.enum(PRINTING_LANGUAGES).optional(),
		limit: z.coerce.number().int().min(10).max(100).optional(),
		offset: z.coerce.number().int().nonnegative().optional(),
		q: z.string().optional(),
		status: z.enum(['released', 'announced']).optional(),
		sortBy: z
			.enum([
				'cardNumber',
				'expansionCode',
				'language',
				'printedNumber',
				'variant'
			])
			.optional(),
		variant: z.enum(PRINTING_VARIANTS).optional()
	}),
	component: PrintingsPage
})

function PrintingsPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const limit = search.limit ?? 50
	const offset = search.offset ?? 0
	const sortBy = search.sortBy ?? 'cardNumber'
	const direction = search.direction ?? 'asc'
	const printingsQuery = useQuery({
		queryKey: ['catalog', 'printings', search],
		queryFn: () =>
			getAdminCatalogPrintings({
				query: {
					direction,
					expansionCode: search.expansionCode,
					language: search.language,
					limit,
					offset,
					q: search.q,
					status: search.status,
					sortBy,
					variant: search.variant
				}
			}).then((response) => response.data)
	})
	const data = printingsQuery.data?.data ?? []
	const hasActiveFilters = Boolean(
		search.expansionCode ||
			search.language ||
			search.q ||
			search.status ||
			search.variant
	)
	const columns: DataTableColumn<(typeof data)[number], typeof sortBy>[] = [
		{
			id: 'image',
			header: t('common:image'),
			cell: (item) => <CatalogThumbnail src={item.imageUrl} />,
			hideable: false,
			width: 76
		},
		{
			id: 'cardNumber',
			header: t('common:card'),
			cell: (item) => (
				<div>
					<p className='font-medium'>
						{item.card.cardNumber ?? 'DON!!'}
					</p>
					<p className='text-muted-foreground text-xs'>
						{item.card.category}
					</p>
				</div>
			),
			sortKey: 'cardNumber',
			width: 180
		},
		{
			id: 'expansionCode',
			header: t('common:expansion'),
			cell: (item) => (
				<div>
					<p className='font-medium'>{item.expansion.code}</p>
					<p className='text-muted-foreground text-xs'>
						{item.expansion.series ?? '—'}
					</p>
				</div>
			),
			sortKey: 'expansionCode',
			width: 190
		},
		{
			id: 'printedNumber',
			header: t('common:printed_number'),
			cell: (item) => item.printedNumber ?? '—',
			sortKey: 'printedNumber',
			width: 155
		},
		{
			id: 'language',
			header: t('common:language'),
			cell: (item) => item.language,
			sortKey: 'language',
			width: 110
		},
		{
			id: 'variant',
			header: t('common:variant'),
			cell: (item) => <Badge variant='secondary'>{item.variant}</Badge>,
			sortKey: 'variant',
			width: 150
		},
		{
			id: 'actions',
			header: t('common:actions'),
			cell: (item) => (
				<div className='flex justify-end gap-2'>
					<CreatePrintingDialog
						printing={{
							card: item.card,
							expansion: item.expansion,
							id: item.id,
							imageAssetId: item.imageAssetId,
							language: item.language,
							printedNumber: item.printedNumber,
							variant: item.variant
						}}
					/>
					<ArchiveCatalogRecordButton id={item.id} type='printing' />
				</div>
			),
			pinned: 'right',
			width: 220,
			minWidth: 220,
			maxWidth: 220,
			enableResizing: false
		}
	]

	function updateSearch(next: Partial<typeof search>) {
		navigate({ to: Route.fullPath, search: { ...search, ...next } })
	}

	return (
		<BackofficeShell contentClassName='max-w-none'>
			<header className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<p className='font-medium text-primary text-sm'>
						{t('common:catalog')}
					</p>
					<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
						{t('common:printings')}
					</h1>
				</div>
				<div>
					<CreatePrintingDialog />
				</div>
			</header>
			<div className='mt-6 flex flex-wrap gap-3'>
				<DebouncedSearchInput
					ariaLabel={t('common:search')}
					className='w-full sm:w-60'
					onValueChange={(q) =>
						updateSearch({ offset: 0, q: q || undefined })
					}
					placeholder={t('common:search')}
					value={search.q ?? ''}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_language')}
					items={[
						{ label: t('common:all_languages'), value: 'all' },
						...PRINTING_LANGUAGES.map((value) => ({
							label: value,
							value
						}))
					]}
					label={t('common:language')}
					onValueChange={(value) =>
						updateSearch({
							language: PRINTING_LANGUAGES.find(
								(item) => item === value
							),
							offset: 0
						})
					}
					value={search.language ?? 'all'}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_variant')}
					items={[
						{ label: t('common:all_variants'), value: 'all' },
						...PRINTING_VARIANTS.map((value) => ({
							label: value,
							value
						}))
					]}
					label={t('common:variant')}
					onValueChange={(value) =>
						updateSearch({
							offset: 0,
							variant: PRINTING_VARIANTS.find(
								(item) => item === value
							)
						})
					}
					value={search.variant ?? 'all'}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_status')}
					items={[
						{ label: t('common:all_statuses'), value: 'all' },
						{ label: t('common:released'), value: 'released' },
						{ label: t('common:announced'), value: 'announced' }
					]}
					label={t('common:status')}
					onValueChange={(value) =>
						updateSearch({
							offset: 0,
							status: PRINTING_STATUSES.find(
								(item) => item === value
							)
						})
					}
					value={search.status ?? 'all'}
				/>
				<ResetFiltersButton
					disabled={!hasActiveFilters}
					onClick={() =>
						updateSearch({
							expansionCode: undefined,
							language: undefined,
							offset: 0,
							q: undefined,
							status: undefined,
							variant: undefined
						})
					}
				/>
			</div>
			<DataTable
				className='mt-6 h-[calc(100vh-17rem)] min-h-96'
				columns={columns}
				data={data}
				pagination={{
					onPageChange: (pageIndex) =>
						updateSearch({ offset: pageIndex * limit }),
					onPageSizeChange: (nextLimit) =>
						updateSearch({ limit: nextLimit, offset: 0 }),
					pageCount: Math.ceil(
						(printingsQuery.data?.meta.total ?? 0) / limit
					),
					pageIndex: Math.floor(offset / limit),
					pageSize: limit
				}}
				sort={{
					onSortChange: (next) =>
						updateSearch({
							direction: next.direction,
							offset: 0,
							sortBy: next.by ?? 'cardNumber'
						}),
					state: { by: sortBy, direction }
				}}
				state={{
					empty: t('common:empty_printings'),
					error: printingsQuery.isError ? (
						<QueryErrorState
							message={t('common:catalog_load_error')}
							onRetry={() => printingsQuery.refetch()}
						/>
					) : (
						false
					),
					loading: printingsQuery.isPending
				}}
			/>
		</BackofficeShell>
	)
}
